/**
 * Утилиты для работы с цветами
 */

// Цвет новой стадии по умолчанию (StageForm) — до того как пользователь выбрал свой
export const DEFAULT_STAGE_COLOR = '#1976d2';
// Цвет нового статуса по умолчанию (StatusForm) — до того как пользователь выбрал свой
export const DEFAULT_STATUS_COLOR = '#3B82F6';

// Кеш для конвертированных цветов (LRU с ограничением размера)
const colorCache = new Map();
const MAX_CACHE_SIZE = 1000;

/**
 * Конвертирует цвет в HEX формат с кешированием
 * @param {string} color - цвет в любом формате (hex, rgb, rgba) - поддерживает как с пробелами, так и без них
 * @returns {string} - цвет в HEX формате
 */
export const convertToHex = (color) => {
  if (!color) return '#6B7280';
  
  // Проверяем кеш
  if (colorCache.has(color)) {
    return colorCache.get(color);
  }
  
  let result;
  
  // Если уже HEX формат, возвращаем как есть
  if (color[0] === '#') {
    result = color;
  } else if (color[0] === 'r') {
    // RGB или RGBA формат
    const isRgba = color[3] === 'a';
    const startIndex = isRgba ? 5 : 4; // "rgba(" или "rgb("
    
    // Извлекаем числа более эффективно
    const numbers = color.match(/\d+/g);
    if (numbers && numbers.length >= 3) {
      const r = parseInt(numbers[0]);
      const g = parseInt(numbers[1]);
      const b = parseInt(numbers[2]);
      
      if (r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255) {
        // Более быстрая конвертация в HEX
        result = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
      } else {
        result = color;
      }
    } else {
      result = color;
    }
  } else {
    result = color;
  }
  
  // Сохраняем в кеш с ограничением размера
  if (colorCache.size >= MAX_CACHE_SIZE) {
    const firstKey = colorCache.keys().next().value;
    colorCache.delete(firstKey);
  }
  colorCache.set(color, result);
  
  return result;
};

/**
 * Очищает кеш цветов
 */
export const clearColorCache = () => {
  colorCache.clear();
};

/**
 * Получает статистику кеша цветов
 * @returns {object} - статистика кеша
 */
export const getColorCacheStats = () => {
  return {
    size: colorCache.size,
    maxSize: MAX_CACHE_SIZE,
    hitRate: colorCache.size > 0 ? 'N/A' : '0%' // В реальном приложении можно добавить счетчики
  };
};

/**
 * Создает стили для статуса с поддержкой разных форматов цветов
 * @param {string} color - цвет статуса
 * @param {object} options - опции стилизации
 * @returns {object} - объект стилей
 */
export const getStatusStyles = (color, options = {}) => {
  const {
    backgroundColorOpacity = 0.1,
    borderOpacity = 0.3,
    textOpacity = 1,
    fallbackColor = '#6B7280'
  } = options;
  
  if (!color) {
    return {
      backgroundColor: `${fallbackColor}${Math.round(backgroundColorOpacity * 255).toString(16).padStart(2, '0')}`,
      color: fallbackColor,
      border: `1px solid ${fallbackColor}${Math.round(borderOpacity * 255).toString(16).padStart(2, '0')}`
    };
  }
  
  const hexColor = convertToHex(color);
  
  return {
    backgroundColor: `${hexColor}${Math.round(backgroundColorOpacity * 255).toString(16).padStart(2, '0')}`,
    color: hexColor,
    border: `1px solid ${hexColor}${Math.round(borderOpacity * 255).toString(16).padStart(2, '0')}`
  };
};

/**
 * Проверяет, является ли цвет светлым
 * @param {string} color - цвет в HEX формате
 * @returns {boolean} - true если цвет светлый
 */
export const isLightColor = (color) => {
  const hexColor = convertToHex(color);
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Используем формулу для определения яркости
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
};

/**
 * Получает контрастный цвет текста для фона
 * @param {string} backgroundColor - цвет фона
 * @returns {string} - цвет текста (#000000 или #ffffff)
 */
export const getContrastTextColor = (backgroundColor) => {
  return isLightColor(backgroundColor) ? '#000000' : '#ffffff';
};

/**
 * Создает полные стили для статуса с автоматическим выбором цвета текста
 * @param {string} color - цвет статуса
 * @param {object} options - опции стилизации
 * @returns {object} - объект стилей
 */
export const getFullStatusStyles = (color, options = {}) => {
  const styles = getStatusStyles(color, options);
  const textColor = getContrastTextColor(styles.color);
  
  return {
    ...styles,
    color: textColor,
    fontWeight: '500'
  };
};
